import { API_BASE_URL, get, patch, postFormData } from './apiClient';
import type { RfqHeaderFacts } from '../utils/rfqHeaderFactRows';

export type RfqStatus =
  | 'pending'
  | 'ready'
  | 'processing'
  | 'processed'
  | 'error'
  | 'nonrfq';

export interface RfqListItemDto {
  _id: string;
  rfqId?: string;
  customerName?: string;
  vesselName?: string;
  subject?: string;
  receivedAt?: string;
  status: RfqStatus | string;
  processingMode: 'auto' | 'manual_upload' | 'portal_auto' | string;
  itemCount: number;
  badge?: string;
  // allow quote linkage to come through without breaking if backend includes
  quoteId?: string | null;
  quotationId?: string | null;
  // Set when the customer name genuinely failed to auto-link/create in the Client List
  // (schema validation, DB error). Null/absent means nothing to flag.
  clientLinkError?: string | null;
  // Why the last quotation generation failed (D99.5): a code, shown via features/rfqInbox/generationError.
  generationError?: { code: string; at?: string } | null;
}

type StatusCountsOld = {
  all?: number;
  pending: number;
  processed: number;
  error: number;
};

type StatusCountsNew = {
  new: number; // frontend label, backend might still be pending
  ready: number;
  processed: number;
  error: number;
  all?: number;
};

export interface RfqListResponse {
  data: RfqListItemDto[];
  total: number;
  page: number;
  limit: number;
  statusCounts: StatusCountsOld | StatusCountsNew | any;
}

export interface RfqAttachmentDto {
  filename: string;
  mimeType: string;
  size?: number;
  storagePath?: string;
  fileUrl?: string | null;
}

export interface RfqEmailAttachmentDto {
  filename: string;
  mimeType: string;
  size?: number;
  storagePath?: string;
  fileUrl?: string;
}

export interface RfqEmailDto {
  from?: string;
  to?: string;
  cc?: string;
  date?: string;
  bodyText?: string;
  bodyHtml?: string;
  rawBodyText?: string;
  rawBodyHtml?: string;
  cleanedBody?: string;
  attachments?: RfqEmailAttachmentDto[];
}

// The detail read also carries the buyer's own RFQ number, port, dates and currency (`RfqHeaderFacts`),
// read from the document at intake: '' where it states none.
export interface RfqDetailDto extends RfqListItemDto, RfqHeaderFacts {
  notes?: string;
  extractedData?: {
    header?: Record<string, unknown>;
    items?: unknown[];
    ocrText?: string;
    [key: string]: unknown;
  };
  attachments?: RfqAttachmentDto[];
  email?: RfqEmailDto;
}

/**
 * RFQ line item
 */
export interface RfqItemDto {
  _id: string;
  itemId: number;
  description: string;
  quantity?: number;
  uom?: string;
  splitQuantity?: number | null;
  splitUom?: string | null;
  splitFormula?: string;
  requestedQuantity?: number | null;
  requestedUnit?: string | null;
  suggestedQuantity?: number | null;
  suggestedUnit?: string | null;
  confidenceScore?: number;
  remarks?: string;
  internalRemark?: string;
  /** The extractor could not read this row, so it was kept as written rather than dropped
   *  (ClickUp 86d4a4u10). `description` is then the raw text of the source row. */
  needsReview?: boolean;
  /** Why, in the extractor's words — "Row 9: missing both quantity and unit". */
  reviewReason?: string;
}

/**
 * List RFQs with optional status filter and paging.
 * NOTE: backend expects internal statuses (pending/ready/processing/processed/error/nonrfq).
 */
export async function fetchRfqList(
  status?: RfqStatus | 'all',
  page = 1,
  limit = 25,
  search?: string
) {
  const params = new URLSearchParams();
  params.set('page', String(page));
  params.set('limit', String(limit));
  if (status && status !== 'all') params.set('status', status);
  if (search && search.trim()) params.set('search', search.trim());

  return get<RfqListResponse>(`/rfqs?${params.toString()}`);
}

// Backend shape: { rfq, email } OR rfq directly
export async function fetchRfqDetail(id: string) {
  const raw = await get<{ rfq: any; email?: any } | RfqDetailDto>(`/rfqs/${id}`);

  if ((raw as any)._id) {
    return raw as RfqDetailDto;
  }

  const { rfq, email } = raw as { rfq: any; email?: any };

  return {
    ...(rfq || {}),
    email,
  } as RfqDetailDto;
}

// Backend shape: plain array or { items: [...] }
export async function fetchRfqItems(id: string) {
  const raw = await get<RfqItemDto[] | { items: RfqItemDto[] }>(`/rfqs/${id}/items`);

  if (Array.isArray(raw)) {
    return raw;
  }

  return (raw as { items: RfqItemDto[] }).items ?? [];
}

/**
 * Update a single RFQ item (row-level edit).
 */
export async function updateRfqItem(id: string, payload: Partial<RfqItemDto>) {
  return patch<RfqItemDto>(`/rfq-items/${id}`, payload);
}

/**
 * Reprocess RFQ by ID (server uses body but we send empty form).
 */
export async function reprocessRfq(id: string) {
  return postFormData<RfqDetailDto>(`/rfqs/${id}/reprocess`, new FormData());
}

/**
 * Manual RFQ creation via file upload.
 */
export async function createManualRfqWithFile(formData: FormData) {
  return postFormData<RfqDetailDto>('/rfqs/manual-upload', formData);
}

export async function createManualRfqWithFileTest(formData: FormData) {
  return postFormData<RfqDetailDto>('/rfqs/manual-upload-test', formData);
}

export async function reuploadRfqWithFile(id: string, formData: FormData) {
  return postFormData<RfqDetailDto>(`/rfqs/${id}/reupload`, formData);
}

/**
 * Delete RFQ – uses native fetch so apiClient stays untouched.
 */
export async function deleteRfq(id: string): Promise<void> {
  const token = localStorage.getItem('token');
  const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};

  const res = await fetch(`${API_BASE_URL}/rfqs/${id}`, {
    method: 'DELETE',
    headers,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `Request failed with status ${res.status}`);
  }
}

/**
 * Update RFQ header/meta (subject, rfqId, customerName, vesselName, notes, status, assignedTo, etc.).
 * This maps to PATCH /api/rfqs/:id
 */
export async function updateRfq(
  id: string,
  payload: Partial<
    Pick<RfqDetailDto, 'rfqId' | 'customerName' | 'vesselName' | 'subject' | 'notes' | 'status'>
  > & {
    assignedTo?: string | null;
  }
) {
  return patch<RfqDetailDto>(`/rfqs/${id}`, payload);
}

/**
 * Copy for a failed vessel save on the RFQ, keyed on the envelope's `code`
 * (invariant 6) — never the server's prose.
 *
 * The vessel is denormalised onto this RFQ's quotation, its confirmed order and
 * every purchase order under it, and the sync owner writes all four inside one
 * transaction. So a failure really does mean nothing changed anywhere, and
 * saying so is the useful half of the message.
 */
export function rfqVesselUpdateErrorMessage(err: unknown): string {
  const code = (err as { code?: unknown } | null)?.code;

  switch (code) {
    case 'RFQ_NOT_FOUND':
      return 'This RFQ no longer exists. Reload the inbox.';
    default:
      return 'The vessel name could not be saved. Nothing has been changed — try again, and contact support if it keeps failing.';
  }
}

/** Copy for a failed client link on the RFQ (D105.13), shown for any failure; never the server's prose. */
export function rfqClientLinkErrorMessage(): string {
  return 'The client could not be linked. Nothing has been changed — try again, and contact support if it keeps failing.';
}

/**
 * Replace ALL items for an RFQ.
 * This maps to PATCH /api/rfqs/:id/items
 */
export async function replaceRfqItems(
  id: string,
  items: Array<
    Pick<RfqItemDto, 'description'> &
      Partial<Pick<RfqItemDto, 'quantity' | 'uom' | 'confidenceScore' | 'remarks' | 'internalRemark'>>
  >
) {
  return patch<{ rfq: RfqDetailDto; items: RfqItemDto[] }>(`/rfqs/${id}/items`, {
    items,
  });
}
